import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getPushStatus,
  enableNotifications,
  refreshBadge,
  isAuthenticated,
  dismissShadeNotification,
  dismissAllShadeNotifications,
  type PushStatus,
} from '@/lib/push-client';
import { api } from '@/lib/api';
import { friendlyPushError } from '@/lib/push-error';
import { isRevisionNotification } from '@/lib/notification-revision';
import { cn } from '@/lib/utils';
import RevisionBadge from './RevisionBadge';

const isIOSDevice = typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: string;
  entity_type: string;
  entity_id: string;
  is_read: number | boolean;
  created_at: string;
}

function notificationHref(n: NotificationItem): string {
  if (n.entity_type === 'requirement' && n.entity_id) return `/requirements/${n.entity_id}`;
  if (n.entity_type === 'actual_status' && n.entity_id) return `/actuals/${n.entity_id}`;
  return '/notifications';
}

/** "5 minutes ago"-style relative time (or date for older items). */
function formatRelativeTime(date: string): string {
  const d = new Date(date);
  if (isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short' }).format(d);
}

function moduleLabel(n: NotificationItem): string {
  if (n.entity_type === 'requirement') return 'Departmental Labour-Requirement';
  if (n.entity_type === 'actual_status') return 'Departmental Labour -Actual';
  return 'Notification';
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pushStatus, setPushStatus] = useState<PushStatus | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const loadNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        api.get<{ data?: NotificationItem[] }>('/notifications?limit=10'),
        api.get<{ data?: { count?: number } }>('/notifications/unread-count'),
      ]);
      setItems(listRes?.data ?? []);
      const count = Math.max(0, Number(countRes?.data?.count ?? 0));
      setUnread(count);
      void refreshBadge(count);
    } catch {
      setItems([]);
      setUnread(0);
      void refreshBadge(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      setPushStatus(await getPushStatus());
    } catch {
      // A failed status probe should not be reported as "Unavailable" (that
      // would wrongly claim the device cannot support push - STEP 11). Keep the
      // previous capability state so the UI only says Unavailable when the
      // device genuinely lacks the API.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return;
    void loadNotifications();
    void loadStatus();
    const onVisible = () => { if (document.visibilityState === 'visible') void loadNotifications(); };
    document.addEventListener('visibilitychange', onVisible);
    const h = window.setInterval(loadNotifications, 15000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(h);
    };
  }, [loadNotifications, loadStatus]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = () => {
    setPushError(null);
    setOpen((o) => !o);
  };

  const doEnable = async () => {
    setPushBusy(true);
    setPushError(null);
    const result = await enableNotifications();
    setPushBusy(false);
    if (result.ok) {
      setPushStatus(await getPushStatus().catch(() => null));
      return;
    }
    setPushError(friendlyPushError(result));
  };

  const markAllRead = async () => {
    setBusy(true);
    try {
      await api.put('/notifications/read-all', {});
      setItems((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
      setUnread(0);
      void refreshBadge(0);
      dismissAllShadeNotifications();
    } catch {
      // Keep the current list; next reload reconciles with the server.
    } finally {
      setBusy(false);
    }
  };

  const openNotification = async (n: NotificationItem) => {
    setOpen(false);
    if (!n.is_read) {
      api.put(`/notifications/${n.id}/read`, {}).catch(() => {});
      const nextUnread = items.filter((x) => x.id !== n.id && !x.is_read).length;
      setUnread(nextUnread);
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: 1 } : x)));
      void refreshBadge(nextUnread);
      dismissShadeNotification(n.entity_type, n.entity_id);
    }
    navigate(notificationHref(n));
  };

  const unreadCount = items.filter((n) => !n.is_read).length;

  const pushEnabled = !!pushStatus?.enabled;
  const pushSupported = !!pushStatus?.supported;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={toggle}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-secondary hover:bg-surface-hover hover:text-primary transition-colors cursor-pointer"
        aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-error px-1 text-[10px] font-semibold leading-none text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40 bg-black/30 md:hidden"
            aria-hidden="true"
            onClick={() => setOpen(false)}
          />
          <div
            role="dialog"
            aria-modal="false"
            aria-label="Notifications"
            className="notification-sheet fixed inset-x-0 bottom-0 z-50 flex flex-col overflow-hidden rounded-t-2xl border border-default bg-surface shadow-xl md:absolute md:inset-auto md:right-0 md:top-full md:mt-2 md:w-[380px] md:rounded-xl"
          >
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-default px-4 py-3">
              <div className="flex min-w-0 items-center gap-2">
                <Bell size={18} className="shrink-0 text-secondary" />
                <p className="truncate font-semibold text-primary">Notifications</p>
                {unreadCount > 0 && (
                  <span className="shrink-0 rounded-full bg-error px-2 py-0.5 text-xs font-semibold text-white">
                    {unreadCount}
                  </span>
                )}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-10 px-3 text-xs"
                    onClick={() => void markAllRead()}
                    disabled={busy}
                    title="Mark all as read"
                  >
                    <CheckCheck size={15} className="mr-1" />
                    Mark all read
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-secondary hover:bg-surface-hover"
                  aria-label="Close notifications"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
              {loading ? (
                <p className="py-10 text-center text-sm text-muted">Loading notifications...</p>
              ) : items.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <p className="text-sm font-medium text-primary">No new notifications</p>
                  <p className="mt-1 text-sm text-secondary">You're all caught up.</p>
                </div>
              ) : (
                <ul className="divide-y divide-default">
                  {items.map((n) => {
                    const unreadItem = !n.is_read;
                    const revision = isRevisionNotification(n);
                    return (
                      <li
                        key={n.id}
                        data-revision={revision ? 'true' : undefined}
                        className={cn(revision && 'notification-revision-item')}
                      >
                        <button
                          type="button"
                          onClick={() => void openNotification(n)}
                          className={cn(
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors',
                            'focus-visible:outline-none cursor-pointer',
                            revision
                              ? 'notification-revision-row'
                              : 'hover:bg-surface-hover focus-visible:bg-surface-hover',
                          )}
                        >
                          <span
                            aria-hidden="true"
                            className={cn(
                              'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                              unreadItem ? 'bg-brand-500' : 'bg-transparent',
                            )}
                          />
                          <span className="min-w-0 flex-1">
                            <span className="flex min-w-0 flex-wrap items-center gap-2">
                              <span
                                className={cn(
                                  'text-sm text-primary [overflow-wrap:anywhere]',
                                  revision ? 'notification-revision-title' : 'font-medium',
                                )}
                              >
                                {n.title}
                              </span>
                              {revision && <RevisionBadge />}
                            </span>
                            <span className="mt-0.5 line-clamp-2 block text-sm text-secondary [overflow-wrap:anywhere]">
                              {n.message}
                            </span>
                            <span className="mt-1 block text-xs text-muted">
                              {moduleLabel(n)} · {formatRelativeTime(n.created_at)}
                            </span>
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <section
              aria-label="Notification settings on this device"
              className="shrink-0 border-t border-default px-4 py-3"
            >
              <p className="text-sm font-medium text-primary">
                Device notifications:{' '}
                <span className={cn('font-semibold', pushEnabled ? 'text-success' : 'text-secondary')}>
                  {pushSupported ? (pushEnabled ? 'ON ✓' : 'OFF') : 'Unavailable'}
                </span>
              </p>

              {pushSupported && pushEnabled ? (
                <p className="mt-1 text-sm text-secondary">
                  Notifications enabled. You'll get alerts even when the app is not open.
                </p>
              ) : pushSupported && !pushEnabled ? (
                <p className="mt-1 text-sm text-secondary">
                  Notifications on this device are off. Turn them on to get alerts even when the
                  app is not open.
                </p>
              ) : (
                <p className="mt-1 text-sm text-secondary">
                  Push notifications are temporarily unavailable.
                </p>
              )}

              {!pushSupported && (
                <p className="mt-0.5 text-xs text-muted">In-app notifications continue to work.</p>
              )}

              {isIOSDevice && pushSupported && !pushStatus?.standalone && (
                <p className="mt-2 rounded-lg border border-warning/20 bg-warning/10 p-3 text-sm text-warning">
                  To get alerts on iPhone/iPad, first add Departmental Labour App to your Home
                  Screen, then enable notifications from the installed app.
                </p>
              )}

              {pushError && (
                <p className="mt-2 text-sm text-error" role="alert">{pushError}</p>
              )}

              {pushSupported && !pushEnabled && (
                <Button
                  size="lg"
                  className="mt-3 w-full"
                  onClick={() => void doEnable()}
                  loading={pushBusy}
                  disabled={pushBusy}
                >
                  <Bell size={16} />
                  Turn On Notifications
                </Button>
              )}
            </section>

            <div className="flex shrink-0 items-center justify-between border-t border-default px-4 py-2.5">
              <span className="text-xs text-muted">
                {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
              </span>
              <button
                type="button"
                onClick={() => { setOpen(false); navigate('/notifications'); }}
                className="inline-flex min-h-11 items-center gap-0.5 px-1 text-sm font-medium text-brand-500 hover:text-brand-600"
                aria-label="View all notifications"
              >
                View All
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}